import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatDateRangePicker } from '@angular/material/datepicker';
import { CallListService } from '../services/call-list.service';
import { formatDate } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { environment } from '../../environments/environment';

// CallListRow interface for table data
interface CallListRow {
  patient_id: number | string;
  last_update: string | Date | null;
  status: string;
  callResultStatusId?: number | null;
  name: string;
  phone: string;
  email: string;
  zipcode: string;
  metroArea: string;
  dob: string;
  interactions: number;
  study: string;
  transfer: string;
  agentName: string;
  agentType: string;
  gender: string;
  poolId: string | number | null;
  activeAgentId?: number;
  cadenceAttemptCount?: number | null;
}

@Component({
  selector: 'app-call-list',
  standalone: false,
  templateUrl: './call-list.component.html',
  styleUrl: './call-list.component.css'
})
export class CallListComponent implements OnInit, OnDestroy {

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('lastUpdatePicker') lastUpdatePicker!: MatDateRangePicker<Date>;
  dataSource!: MatTableDataSource<CallListRow>;
  callListData: CallListRow[] = [];
  totalCount = 0;
  pageIndex = 0;
  pageSize = 10;
  isLoading = false;
  sortBy: string | null = null;
  sortOrder: 'asc' | 'desc' = 'asc';
  searchTerm = '';
  searchInput = '';
  comtrakURL = environment.comtrakURL;
  private readonly destroy$ = new Subject<void>();
  
  // Column filters - now supports multiple values per column
  columnFilters: { [key: string]: string[] } = {};
  activeFilterColumn: string | null = null;
  tempFilterValues: { [key: string]: string } = {};
  popupPosition: { top: string; left: string } = { top: '0px', left: '0px' };
  dateRangeFilters: { [key: string]: { start: Date | null; end: Date | null } } = {
    last_update: { start: null, end: null }
  };
  
  // Dropdown filter options
  dropdownOptions: { [key: string]: string[] } = {
    status: [
      'HungUp',
      'LeftVoiceMail', 
      'BadContact',
      "CallFailed",
      'NotInterested',
      'Scheduled',
      'RequestedCallBack',
      'HardDNQ',
      'StudiesDNQ',
      'Transferred'
    ],
    gender: [
      'Male',
      'Female'
    ],
    agentName: [
      'EVA',
      'Mass Agent - Fresh',
      'Mass Agent - DB'
    ],
    metroArea: [
      'ABQ',
      'BOS',
      'CHI',
      'DFW',
      'DET',
      'HOU',
      'NJ',
      'NYC',
      'PHX',
      'PHL',
      'RNO',
      'SAN',
      'SEA',
      'MFL'
    ]
  };
 
  
  
  displayedColumns = [
    'patient_id',
    'last_update',
    'status',
    'name',
    'cadenceAttemptCount',
    'poolId',
    'phone',
    'email',
    'zipcode',
    'metroArea',
    'dob',
    'interactions',
    'study',
    'transfer',
    'agentName',
    'agentType',
    'gender'
  ];

  constructor(private callListService: CallListService) {}

  ngOnInit(): void {
    this.dataSource = new MatTableDataSource<CallListRow>([]);
    this.loadPage(1, this.pageSize);
  }

  onSearch(): void {
    this.searchTerm = this.searchInput.trim();
    this.pageIndex = 0;
    this.loadPage(1, this.pageSize);
  }

  onClearSearch(): void {
    this.searchInput = '';
    this.searchTerm = '';
    this.pageIndex = 0;
    this.loadPage(1, this.pageSize);
  }

  ngAfterViewInit(): void {
    // Bind paginator and sort
    this.dataSource.sort = this.sort;
    this.sort.sortChange
      .pipe(takeUntil(this.destroy$))
      .subscribe((sort) => {
        if (sort.direction === '') {
          this.sortBy = null;
          this.sortOrder = 'asc';
        } else {
          this.sortBy = sort.active;
          this.sortOrder = sort.direction === 'desc' ? 'desc' : 'asc';
        }
        this.pageIndex = 0;
        this.loadPage(1, this.pageSize, this.sortBy, this.sortOrder);
      });
  }

  // Optional: Add filtering
  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = (filterValue || '').trim().toLowerCase();
  }

  loadPage(page: number, pageSize: number, sortBy?: string | null, sortOrder?: 'asc' | 'desc' | null): void {
    const by = sortBy ?? this.sortBy;
    const order = sortOrder ?? this.sortOrder;
    this.isLoading = true;
    this.callListService.getCallList(page, pageSize, this.columnFilters, by, order, this.searchTerm).subscribe({
      next: (res) => {
        this.totalCount = res.totalCount || 0;
        const mapped: CallListRow[] = (res.data || []).map((r: any) => ({
          patient_id: r.entityId,
          last_update: (r.updatedAt || r.createdAt) ? (r.updatedAt || r.createdAt) : null,
          status: r.callResultStatus || r.status,
          callResultStatusId: (typeof r.callResultStatusId !== 'undefined') ? r.callResultStatusId : null,
          name: r.name,
          phone: r.phoneNumber,
          email: r.email,
          zipcode: (r.zipCode || '').toString().trim(),
          metroArea: r.metroArea || '-',
          dob: r.dateOfBirth ? formatDate(r.dateOfBirth, 'MM/dd/yyyy', 'en-US') : '',
          interactions: (r.attempts ? r.attempts.length : 0),
          study: 'None',
          transfer: r.callResultStatus === 'Transferred' ? 'Transfer Successful' : 'No Transfer',
          agentName: r.agentName || 'Database Leads Agent',
          agentType: r.agentType || 'MassCalling',
          gender: r.gender || '-',
          poolId: r.poolId ?? null,
          activeAgentId: r.activeAgentId || 4,
          cadenceAttemptCount: typeof r.cadenceAttemptCount === 'number' ? r.cadenceAttemptCount : null
        }));
        this.callListData = mapped;
        this.dataSource.data = mapped;
        // Rebind sort and paginator after async data update to ensure sorting works
        this.dataSource.sort = this.sort;
      },
      error: () => {
        this.dataSource.data = [];
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  onPageChange(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadPage(this.pageIndex + 1, this.pageSize);
  }

  formatLocalDateTime(date: string | Date | null): string {
    if (!date) return '';
    
    let dateObj: Date;
    
    if (typeof date === 'string') {
      // API returns date in format: 2026-01-08T20:11:19.047 (no timezone indicator)
      // We need to explicitly parse this as UTC time
      let dateString = date.trim();
      
      // If the string doesn't end with Z or timezone offset, treat it as UTC
      if (!dateString.endsWith('Z') && !dateString.match(/[+-]\d{2}:\d{2}$/)) {
        // Parse the date string explicitly as UTC
        // Format: YYYY-MM-DDTHH:mm:ss.sss
        const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?$/);
        if (match) {
          const [, year, month, day, hour, minute, second, milliseconds] = match;
          // Create Date using UTC values - this explicitly creates a UTC date
          // When we use get* methods later, they'll return local time
          dateObj = new Date(Date.UTC(
            parseInt(year, 10),
            parseInt(month, 10) - 1, // month is 0-indexed
            parseInt(day, 10),
            parseInt(hour, 10),
            parseInt(minute, 10),
            parseInt(second, 10),
            milliseconds ? parseInt(milliseconds.padEnd(3, '0').substring(0, 3), 10) : 0
          ));
        } else {
          // Fallback: append 'Z' and parse
          dateString = dateString.split('.')[0] + 'Z';
          dateObj = new Date(dateString);
        }
      } else {
        // Already has timezone info, parse normally
        dateObj = new Date(dateString);
      }
    } else {
      dateObj = date;
    }
    
    // Check if dateObj is valid
    if (isNaN(dateObj.getTime())) return '';
    
    // Use Date object's local time methods - these automatically return values in browser's local timezone
    // Since we created the Date using UTC values, get* methods will return local time based on system timezone
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const year = dateObj.getFullYear();
    const hours = dateObj.getHours();
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    
    return `${month}/${day}/${year}, ${displayHours}:${minutes} ${ampm}`;
  }

  // Column filtering methods
  openColumnFilter(column: string, event: Event): void {
    // Prevent event bubbling to avoid triggering sort
    event.stopPropagation();
    event.preventDefault();
    
    this.activeFilterColumn = this.activeFilterColumn === column ? null : column;
    this.tempFilterValues[column] = '';
    
    if (column === 'last_update') {
      this.syncDateRangeFromFilters(column);
    }

    if (this.activeFilterColumn) {
      // Calculate position for the popup
      const target = event.target as HTMLElement;
      const rect = target.getBoundingClientRect();
      
      // For the last column (gender), center the popup under the filter icon
      const isLastColumn = column === 'gender';
      const popupWidth = 300; // Approximate popup width
      
      this.popupPosition = {
        top: (rect.bottom + window.scrollY + 4) + 'px',
        left: isLastColumn 
          ? (rect.left + window.scrollX + (rect.width / 2) - (popupWidth / 2)) + 'px'
          : (rect.left + window.scrollX) + 'px'
      };
    }
  }

  addFilterValue(column: string, value: string): void {
    if (value && value !== '') {
      // Allow spaces in filter values, only trim leading/trailing whitespace
      const cleanValue = value.trim();
      if (cleanValue !== '') {
        if (!this.columnFilters[column]) {
          this.columnFilters[column] = [];
        }
        if (!this.columnFilters[column].includes(cleanValue)) {
          this.columnFilters[column].push(cleanValue);
        }
        this.tempFilterValues[column] = '';
        this.pageIndex = 0; // Reset to first page when filtering
        this.loadPage(1, this.pageSize);
      }
    }
  }

  removeFilterValue(column: string, value: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    if (this.columnFilters[column]) {
      this.columnFilters[column] = this.columnFilters[column].filter(v => v !== value);
      if (this.columnFilters[column].length === 0) {
        delete this.columnFilters[column];
      }
      this.pageIndex = 0; // Reset to first page when filtering
      this.loadPage(1, this.pageSize);
    }
  }

  clearColumnFilter(column: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    if (this.dateRangeFilters[column]) {
      this.resetDateRange(column);
    }

    delete this.columnFilters[column];
    this.pageIndex = 0; // Reset to first page when clearing filter
    this.loadPage(1, this.pageSize);
  }

  clearAllFilters(): void {
    this.columnFilters = {};
    Object.keys(this.dateRangeFilters).forEach(key => {
      this.resetDateRange(key);
    });
    this.pageIndex = 0;
    this.loadPage(1, this.pageSize);
  }

  hasActiveFilters(): boolean {
    return Object.keys(this.columnFilters).length > 0;
  }

  getFilterValues(column: string): string[] {
    return this.columnFilters[column] || [];
  }

  getTempFilterValue(column: string): string {
    return this.tempFilterValues[column] || '';
  }

  setTempFilterValue(column: string, value: string): void {
    this.tempFilterValues[column] = value;
  }

  onInputChange(column: string, event: Event): void {
    const target = event.target as HTMLInputElement;
    this.setTempFilterValue(column, target.value);
  }

  onKeyDown(column: string, event: KeyboardEvent): void {
    // Prevent space key from triggering sort
    if (event.code === 'Space') {
      event.stopPropagation();
    }
  }

  onInputClick(event: Event): void {
    // Prevent click from triggering sort
    event.stopPropagation();
  }

  onInputFocus(event: Event): void {
    // Prevent focus from triggering sort
    event.stopPropagation();
  }

  // Close popup when clicking outside
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.filter-popup') && !target.closest('.filter-icon') && !target.closest('.cdk-overlay-pane')) {
      this.activeFilterColumn = null;
    }
  }

  // Methods for dropdown filters
  getUniqueValues(column: string): string[] {
    if (this.dropdownOptions[column]) {
      return this.dropdownOptions[column];
    }
    
    const uniqueValues = new Set<string>();
    this.callListData.forEach(row => {
      const value = this.getColumnValue(row, column);
      if (value && value.trim() !== '') {
        uniqueValues.add(value.trim());
      }
    });
    
    const sortedValues = Array.from(uniqueValues).sort();
    this.dropdownOptions[column] = sortedValues;
    return sortedValues;
  }

  getColumnValue(row: CallListRow, column: string): string {
    switch (column) {
      case 'status':
        return row.status || '';
      case 'metroArea':
        return row.metroArea || '';
      case 'agentName':
        return row.agentName || '';
      case 'gender':
        return row.gender || '';
      default:
        return '';
    }
  }

  onDropdownFilterChange(column: string, value: string): void {
    if (value && value !== '') {
      if (!this.columnFilters[column]) {
        this.columnFilters[column] = [];
      }
      if (!this.columnFilters[column].includes(value)) {
        this.columnFilters[column].push(value);
      }
      this.pageIndex = 0; // Reset to first page when filtering
      this.loadPage(1, this.pageSize);
    }
  }

  onSelectChange(column: string, event: Event): void {
    const target = event.target as HTMLSelectElement;
    const value = target.value;
    if (value && value !== '') {
      this.onDropdownFilterChange(column, value);
      target.value = ''; // Reset the select to placeholder
    }
  }

  isDropdownColumn(column: string): boolean {
    return ['status', 'metroArea', 'agentName', 'gender'].includes(column);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  hasCompleteDateRange(column: string): boolean {
    const range = this.dateRangeFilters[column];
    return !!(range && range.start && range.end);
  }

  applyDateRangeFilter(column: string): void {
    const range = this.dateRangeFilters[column];
    if (range && range.start && range.end) {
      const start = formatDate(range.start, 'yyyy-MM-dd', 'en-US');
      const end = formatDate(range.end, 'yyyy-MM-dd', 'en-US');
      this.columnFilters[column] = [start, end];
      this.pageIndex = 0;
      this.loadPage(1, this.pageSize);
      // Don't close popup - let user see the filter tag and use Clear button if needed
    }
  }

  clearDateRangeFilter(column: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.resetDateRange(column);
    delete this.columnFilters[column];
    this.pageIndex = 0;
    this.loadPage(1, this.pageSize);
  }

  private resetDateRange(column: string): void {
    if (!this.dateRangeFilters[column]) {
      this.dateRangeFilters[column] = { start: null, end: null };
    } else {
      this.dateRangeFilters[column].start = null;
      this.dateRangeFilters[column].end = null;
    }
  }

  private syncDateRangeFromFilters(column: string): void {
    const existing = this.columnFilters[column];
    if (existing && existing.length === 2) {
      const [start, end] = existing;
      this.dateRangeFilters[column] = {
        start: start ? new Date(start) : null,
        end: end ? new Date(end) : null
      };
    } else {
      this.resetDateRange(column);
    }
  }

  getDateRangeDisplay(column: string): string {
    const values = this.columnFilters[column];
    if (values && values.length === 2) {
      try {
        const startLabel = formatDate(values[0], 'MM/dd/yyyy', 'en-US');
        const endLabel = formatDate(values[1], 'MM/dd/yyyy', 'en-US');
        return `${startLabel} - ${endLabel}`;
      } catch {
        return `${values[0]} - ${values[1]}`;
      }
    }
    return '';
  }

  onDateRangePickerClosed(column: string): void {
    // When picker closes, apply the filter if dates were selected
    // This will only apply if Apply was clicked (dates will be bound via ngModel)
    // If Cancel was clicked, dates won't be updated, so this won't apply
    this.applyDateRangeFilter(column);
  }
}
